import React, { FC } from 'react';

import {
    Button, message, Popconfirm, Typography,
} from 'antd';
import { observer } from 'mobx-react';
import { DeleteOutlined, CopyOutlined } from '@ant-design/icons';

import i18n from '@/content';
import { useStores } from '../../../hooks';
import styles from './PolicyButtons.module.scss';

const { Text } = Typography;

const WARNING_MESSAGE_DURATION = 8;
const nameSpace = 'policyEditor';

/**
 * Policy editor action buttons (Save/Apply, Delete, Duplicate)
 */
const PolicyButtons: FC = observer(() => {
    const { policyStore } = useStores();
    const {
        selectedPolicy,
        hasUnsavedChanges,
        validateDaysRetention,
        validateLimitToUsers,
    } = policyStore;

    if (!selectedPolicy) {
        return null;
    }

    const isNewPolicy = !selectedPolicy.id;
    const hasChanges = hasUnsavedChanges();
    const daysValidationError = validateDaysRetention();
    const isDaysValid = !daysValidationError;
    const limitToUsersValidationError = validateLimitToUsers();
    const isLimitToUsersValid = !limitToUsersValidationError;

    const handleSubmit = async (): Promise<void> => {
        message.destroy();

        const result = await policyStore.savePolicy();

        if (result.success) {
            const messageKey = isNewPolicy ? 'savedSuccess' : 'updatedSuccess';
            message.success(i18n.t(`${nameSpace}.messages.${messageKey}`));
        } else if (result.errorCode === 400) {
            // Validation errors - show as warning
            message.warning(
                i18n.t(result.error),
                WARNING_MESSAGE_DURATION,
            );
        } else {
            // Other errors - show as error
            message.error(i18n.t(result.error));
        }
    };

    const handleDelete = async (): Promise<void> => {
        if (!selectedPolicy.id) {
            return;
        }

        const result = await policyStore.deletePolicy(selectedPolicy.id);

        if (result.success) {
            message.success(i18n.t(`${nameSpace}.messages.deleteSuccess`));
        } else {
            message.error(i18n.t(result.error));
        }
    };

    const handleDuplicate = (): void => {
        policyStore.duplicatePolicy();
    };

    const isApplyDisabled = !hasChanges || !isDaysValid || !isLimitToUsersValid;
    const isDeleteDisabled = !selectedPolicy.deletable;

    return (
        <div className={styles['policy-settings-buttons']}>
            {isNewPolicy ? (
                <Button
                    type="primary"
                    disabled={!isDaysValid || !isLimitToUsersValid}
                    onClick={handleSubmit}
                    className={styles['primary-button']}
                    data-testid="policyEditor_applyChagesNewPolicy"
                >
                    {i18n.t('general.buttons.save')}
                </Button>
            ) : (
                <>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        disabled={isApplyDisabled}
                        className={styles['primary-button']}
                        data-testid="policyEditor_applyChangesEditPolicy"
                    >
                        {i18n.t('general.buttons.apply')}
                    </Button>
                    <Popconfirm
                        placement="topLeft"
                        disabled={isDeleteDisabled}
                        title={<Text>{i18n.t(`${nameSpace}.deleteConfirm`)}</Text>}
                        onConfirm={handleDelete}
                        okText={i18n.t('general.buttons.confirm')}
                        cancelText={i18n.t('general.buttons.cancel')}
                        okButtonProps={{ 'data-testid': 'PolicyEditor_deleteButton_popupConfirm' }}
                    >
                        <Button
                            disabled={isDeleteDisabled}
                            type="link"
                            className={styles['popup-trigger-button']}
                            data-testid="PolicyEditor_deleteButton"
                        >
                            <DeleteOutlined className={styles.icon} />
                            {i18n.t('general.buttons.delete')}
                        </Button>
                    </Popconfirm>

                    <Button
                        type="link"
                        onClick={handleDuplicate}
                        className={styles['secondary-button']}
                    >
                        <CopyOutlined className={styles.icon} />
                        {i18n.t(`${nameSpace}.duplicate`)}
                    </Button>
                </>
            )}
        </div>
    );
});

export default PolicyButtons;
